'use client';

import { c as _c } from "react/compiler-runtime";
import Image from 'next/image';
import { cx } from 'class-variance-authority';
import { LuArrowUpRight, LuBadgeCheck, LuEye, LuMapPin, LuTag, LuTruck } from 'react-icons/lu';
import type { IconType } from 'react-icons';
import { HoverTooltip } from './HoverTooltip';
import { addressCityLabel } from '~/features/activities/addressCityLabel';
import { cityCase } from '../utils/cityCase';
import { useI18n } from '~/features/i18n';
import { useVATRate } from '~/features/portals/money';
import { Units } from '~/modules/units';
import type { PartnerBadge, RankedPartner } from '../utils/partnerRanking';
import { LOCATION_SECTION_ANCHOR_ID } from '../types';
type Props = {
  ranked: RankedPartner;
  /** City the user searched from, for the "X km van {label}" line. */
  userLabel: string | null;
  /** Lightweight non-card row for the additional "other options". */
  minimal?: boolean;
  /**
   * Replaces the distance line with what this provider's trip costs. Named for its content
   * rather than its slot: the icon beside the line has to match the sentence, and a generic
   * "detail" left the transport amount sitting under a map pin.
   */
  transportLine?: string;
  /** True for the partner of the PDP being viewed — rendered as a plain (non-link) card. */
  isCurrent?: boolean;
  /** Notifies the parent which partner is hovered, so the map can emphasise its pin. */
  onHoverChange?: (activityId: number | null) => void;
  className?: string;
};
function badgeLabel(i18n: ReturnType<typeof useI18n>, badge: PartnerBadge): string {
  switch (badge) {
    case 'recommended':
      return i18n._('pdp.location.badge.recommended', undefined, {
        message: 'Recommended'
      });
    case 'closest':
      return i18n._('pdp.location.badge.closest', undefined, {
        message: 'Closest'
      });
    case 'cheapest':
      return i18n._('pdp.location.badge.cheapest', undefined, {
        message: 'Cheapest'
      });
    default:
      {
        const _exhaustive: never = badge;
        return _exhaustive;
      }
  }
}

/**
 * What each badge actually means. "Recommended" in particular read as a mystery ranking —
 * it is Funkey's preferred provider for this activity, and saying so is the difference
 * between a label the user trusts and one they have to guess at.
 */
function badgeExplanation(i18n: ReturnType<typeof useI18n>, badge: PartnerBadge): string {
  switch (badge) {
    case 'recommended':
      return i18n._('pdp.location.badge.recommended.explainer', undefined, {
        message: 'Our preferred provider for this activity.'
      });
    // prettier-ignore
    case 'closest':
      return i18n._('pdp.location.badge.closest.explainer', undefined, {
        message: 'Closest to the location you entered.'
      });
    // prettier-ignore
    case 'cheapest':
      return i18n._('pdp.location.badge.cheapest.explainer', undefined, {
        message: 'Lowest total for your group, transport included.'
      });
    // prettier-ignore
    default:
      {
        const _exhaustive: never = badge;
        return _exhaustive;
      }
  }
}

/**
 * One ranked partner offering, rendered partner-anonymously: city + distance +
 * "vanaf" price + badges. Links to the same activity for that partner via
 * `?id=` in a NEW TAB so the user can compare across tabs. Never renders the
 * partner name or exact address.
 */
export const PartnerResultCard: React.FC<Props> = t0 => {
  const $ = _c(80);
  const {
    ranked,
    userLabel,
    minimal,
    transportLine,
    isCurrent,
    onHoverChange,
    className
  } = t0;
  const i18n = useI18n();
  const vat = useVATRate();
  let t1;
  if ($[0] !== ranked.closestAddress?.city || $[1] !== ranked.closestAddress?.name || $[2] !== ranked.partner.partnerCity) {
    t1 = ranked.closestAddress?.city ?? addressCityLabel(ranked.closestAddress?.name) ?? ranked.partner.partnerCity;
    $[0] = ranked.closestAddress?.city;
    $[1] = ranked.closestAddress?.name;
    $[2] = ranked.partner.partnerCity;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = cityCase(t1);
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const cityLabel = t2;
  let t3;
  if ($[6] !== ranked.cheapestPerPerson || $[7] !== vat) {
    t3 = ranked.cheapestPerPerson > 0 ? vat.calcEUR(ranked.cheapestPerPerson) : null;
    $[6] = ranked.cheapestPerPerson;
    $[7] = vat;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const price = t3;
  let t4;
  if ($[9] !== i18n || $[10] !== ranked.distanceKm || $[11] !== userLabel) {
    t4 = ranked.distanceKm != null ? userLabel ? i18n._("pdp.location.card.distance_from", {
      km: Math.round(ranked.distanceKm),
      city: userLabel
    }, {
      message: "\u2248 {km} km from {city}"
    }) : i18n._("pdp.location.card.distance", {
      km: Math.round(ranked.distanceKm)
    }, {
      message: "\u2248 {km} km"
    }) : null;
    $[9] = i18n;
    $[10] = ranked.distanceKm;
    $[11] = userLabel;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const distanceLabel = t4;
  let t5;
  if ($[13] !== i18n || $[14] !== price) {
    t5 = price != null && <p className="text-sm text-blue-900">{i18n._("pdp.location.card.from", undefined, {
        message: "from"
      })}{" "}<span className="font-semibold text-tertiary-600">{Units.formatEUR(price * 100)}</span><span className="ml-0.5 text-xs font-normal text-blue-900/60">{i18n._("pdp.location.card.per_person", undefined, {
          message: "p.p."
        })}</span></p>;
    $[13] = i18n;
    $[14] = price;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const priceLabel = t5;
  const t6 = `?id=${ranked.partner.activityId}#${LOCATION_SECTION_ANCHOR_ID}`;
  let t7;
  if ($[16] !== onHoverChange || $[17] !== ranked.partner.activityId) {
    t7 = () => onHoverChange?.(ranked.partner.activityId);
    $[16] = onHoverChange;
    $[17] = ranked.partner.activityId;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== onHoverChange) {
    t8 = () => onHoverChange?.(null);
    $[19] = onHoverChange;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== ranked.partner.activityId || $[22] !== t6 || $[23] !== t7 || $[24] !== t8) {
    t9 = {
      href: t6,
      target: "_blank",
      rel: "noopener noreferrer",
      "data-testid": "pdp-location-partner-card",
      "data-activity-id": ranked.partner.activityId,
      onMouseEnter: t7,
      onMouseLeave: t8
    };
    $[21] = ranked.partner.activityId;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const linkProps = t9 as const;
  if (minimal) {
    let t10;
    if ($[26] !== className) {
      t10 = cx("group flex items-center gap-3", className);
      $[26] = className;
      $[27] = t10;
    } else {
      t10 = $[27];
    }
    let t11;
    if ($[28] !== ranked.partner.imagePath) {
      t11 = ranked.partner.imagePath && <Image src={ranked.partner.imagePath} alt="" width={56} height={56} loading="lazy" className="size-14 shrink-0 rounded-lg object-cover" />;
      $[28] = ranked.partner.imagePath;
      $[29] = t11;
    } else {
      t11 = $[29];
    }
    let t12;
    if ($[30] !== cityLabel) {
      t12 = !!cityLabel && <p className="truncate text-sm font-medium text-blue-900">{cityLabel}</p>;
      $[30] = cityLabel;
      $[31] = t12;
    } else {
      t12 = $[31];
    }
    let t13;
    if ($[32] !== distanceLabel || $[33] !== transportLine) {
      t13 = !!(transportLine ?? distanceLabel) && <p className="text-xs text-blue-900/60">{transportLine ?? distanceLabel}</p>;
      $[32] = distanceLabel;
      $[33] = transportLine;
      $[34] = t13;
    } else {
      t13 = $[34];
    }
    let t14;
    if ($[35] !== priceLabel || $[36] !== t12 || $[37] !== t13) {
      t14 = <div className="flex min-w-0 flex-1 flex-col gap-0.5">{t12}{t13}{priceLabel}</div>;
      $[35] = priceLabel;
      $[36] = t12;
      $[37] = t13;
      $[38] = t14;
    } else {
      t14 = $[38];
    }
    let t15;
    if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <LuArrowUpRight className="size-4 shrink-0 text-blue-900/50 opacity-0 transition-opacity group-hover:opacity-100" />;
      $[39] = t15;
    } else {
      t15 = $[39];
    }
    let t16;
    if ($[40] !== linkProps || $[41] !== t10 || $[42] !== t11 || $[43] !== t14) {
      t16 = <a {...linkProps} className={t10}>{t11}{t14}{t15}</a>;
      $[40] = linkProps;
      $[41] = t10;
      $[42] = t11;
      $[43] = t14;
      $[44] = t16;
    } else {
      t16 = $[44];
    }
    return t16;
  }
  let t10;
  if ($[45] !== i18n || $[46] !== isCurrent || $[47] !== ranked.badges) {
    t10 = !!(isCurrent || ranked.badges.length) && <div className="flex flex-wrap items-center gap-1">{isCurrent && <span className={cx("inline-flex h-5 items-center gap-1 rounded-full px-2 text-[10px] font-semibold leading-3", "bg-tertiary-600 text-white")}><LuEye className="size-3 shrink-0" /><span>{i18n._("pdp.location.badge.current", undefined, {
            message: "You're viewing this one"
          })}</span></span>}{ranked.badges.map(badge => {
        const Icon = BADGE_ICON[badge];
        return <HoverTooltip key={badge} content={badgeExplanation(i18n, badge)} position="top" triggerClassName="inline-flex"><span className={cx("inline-flex h-5 items-center gap-1 rounded-full px-2 text-[10px] font-semibold leading-3", BADGE_STYLE[badge])}><Icon className="size-3 shrink-0" /><span>{badgeLabel(i18n, badge)}</span></span></HoverTooltip>;
      })}</div>;
    $[45] = i18n;
    $[46] = isCurrent;
    $[47] = ranked.badges;
    $[48] = t10;
  } else {
    t10 = $[48];
  }
  const badgeRow = t10;
  let t11;
  if ($[49] !== cityLabel) {
    t11 = !!cityLabel && <p className="text-sm font-semibold text-blue-900">{cityLabel}</p>;
    $[49] = cityLabel;
    $[50] = t11;
  } else {
    t11 = $[50];
  }
  let t12;
  if ($[51] !== distanceLabel || $[52] !== transportLine) {
    t12 = !!(transportLine ?? distanceLabel) && <p className="flex items-center gap-1 text-xs text-blue-900/60">{transportLine ? <LuTruck className="size-3.5 shrink-0" /> : <LuMapPin className="size-3.5 shrink-0" />}{transportLine ?? distanceLabel}</p>;
    $[51] = distanceLabel;
    $[52] = transportLine;
    $[53] = t12;
  } else {
    t12 = $[53];
  }
  let t13;
  if ($[54] !== badgeRow || $[55] !== priceLabel || $[56] !== t11 || $[57] !== t12) {
    t13 = <div className="flex flex-col gap-1">{t11}{badgeRow}{t12}{priceLabel}</div>;
    $[54] = badgeRow;
    $[55] = priceLabel;
    $[56] = t11;
    $[57] = t12;
    $[58] = t13;
  } else {
    t13 = $[58];
  }
  const body = t13;
  let t14;
  let t15;
  let t16;
  let thumbnail;
  if ($[59] !== body || $[60] !== className || $[61] !== isCurrent || $[62] !== linkProps || $[63] !== ranked.partner.imagePath) {
    t16 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const shellClassName = cx("flex items-center gap-3", className);
      let t17;
      if ($[68] !== ranked.partner.imagePath) {
        t17 = ranked.partner.imagePath ? <Image src={ranked.partner.imagePath} alt="" width={80} height={80} loading="lazy" className="size-20 shrink-0 rounded-xl object-cover" /> : null;
        $[68] = ranked.partner.imagePath;
        $[69] = t17;
      } else {
        t17 = $[69];
      }
      thumbnail = t17;
      if (isCurrent) {
        let t18;
        if ($[70] !== body) {
          t18 = <div className="min-w-0 flex-1">{body}</div>;
          $[70] = body;
          $[71] = t18;
        } else {
          t18 = $[71];
        }
        t16 = <div data-testid="pdp-location-partner-card" className={shellClassName}>{thumbnail}{t18}</div>;
        break bb0;
      }
      t14 = linkProps;
      t15 = cx("group", shellClassName);
    }
    $[59] = body;
    $[60] = className;
    $[61] = isCurrent;
    $[62] = linkProps;
    $[63] = ranked.partner.imagePath;
    $[64] = t14;
    $[65] = t15;
    $[66] = t16;
    $[67] = thumbnail;
  } else {
    t14 = $[64];
    t15 = $[65];
    t16 = $[66];
    thumbnail = $[67];
  }
  if (t16 !== Symbol.for("react.early_return_sentinel")) {
    return t16;
  }
  let t17;
  if ($[72] !== body) {
    t17 = <div className="min-w-0 flex-1">{body}</div>;
    $[72] = body;
    $[73] = t17;
  } else {
    t17 = $[73];
  }
  let t18;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <LuArrowUpRight className="size-4 shrink-0 text-blue-900/50 opacity-0 transition-opacity group-hover:opacity-100" />;
    $[74] = t18;
  } else {
    t18 = $[74];
  }
  let t19;
  if ($[75] !== t14 || $[76] !== t15 || $[77] !== t17 || $[78] !== thumbnail) {
    t19 = <a {...t14} className={t15}>{thumbnail}{t17}{t18}</a>;
    $[75] = t14;
    $[76] = t15;
    $[77] = t17;
    $[78] = thumbnail;
    $[79] = t19;
  } else {
    t19 = $[79];
  }
  return t19;
};
const BADGE_STYLE: Record<PartnerBadge, string> = {
  recommended: 'bg-blue-900 text-white',
  closest: 'bg-turqoise-500/15 text-turqoise-700',
  cheapest: 'bg-gray-100 text-blue-900/70'
};
const BADGE_ICON: Record<PartnerBadge, IconType> = {
  recommended: LuBadgeCheck,
  closest: LuMapPin,
  cheapest: LuTag
};